import {
  Button,
  Card,
  Input,
  message,
  Popconfirm,
  Select,
  Spin,
  Table,
  TableColumnsType,
} from 'antd';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getUserDetails,
  makeGetCall,
  makePostCall,
} from '../../utils/helper';
import './manage-nodes.css';
import { DeleteOutlined, EditOutlined, SearchOutlined } from '@ant-design/icons';
import EditNode from './EditNode';
import ErrorBoundary from '../../components/ErrorBoundary';

const { Search } = Input;
const { Option } = Select;

// Type definitions
interface Unit {
  unit_code: string;
  unit_label: string;
  value?: string;
  label?: string;
}

interface Modality {
  en_id: string;
  en_node_name: string;
  en_ip_address: string;
  en_port: string;
  en_ae_title: string;
  en_location: string;
  en_operations: string;
  en_comments: string;
}

interface ModalityListState {
  loading: boolean;
  data: Modality[];
  error: string | null;
}

interface LoadingState {
  units: boolean;
  error: string | null;
}

interface APIResponse<T> {
  data?: {
    data?: T;
    success?: boolean;
    message?: string;
  };
  success?: boolean;
  message?: string;
}

interface UserDetails {
  username: string;
} // Modal for editing modality

/**
 * ModalityList Component
 *
 * A comprehensive modality management interface that provides:
 * - List view of all DICOM modalities/nodes
 * - Search and filtering capabilities
 * - CRUD operations (Create, Read, Update, Delete)
 * - Error handling and loading states
 * - Responsive design with accessibility features
 *
 * @component
 * @returns {JSX.Element} The modality list management interface
 */
const ModalityList: React.FC = () => {
  // Ref to track component mount status for cleanup
  const isMountedRef = useRef<boolean>(true);
  const abortControllerRef = useRef<AbortController | null>(null);

  const [modalityList, setModalityList] = useState<ModalityListState>({
    loading: true,
    data: [],
    error: null
  });
  const [deleteInProgress, setDeleteInProgress] = useState<boolean>(false);
  const [editModalVisible, setEditModalVisible] = useState<boolean>(false);
  const [selectedModality, setSelectedModality] = useState<Modality | null>(null);
  const [unitsList, setUnitsList] = useState<Unit[]>([]);
  const [unitsMapping, setUnitsMapping] = useState<Record<string, string>>({});
  const [loadingStates, setLoadingStates] = useState<LoadingState>({
    units: false,
    error: null
  });

  // Filter states
  const [searchText, setSearchText] = useState<string>('');
  const [selectedUnit, setSelectedUnit] = useState<string | null>(null);
  const [selectedOperation, setSelectedOperation] = useState<string | null>(null);

  /**
   * Fetches the list of modalities from the server
   *
   * @async
   * @function fetchModalities
   * @returns {Promise<void>} Promise that resolves when modalities are loaded
   * @throws {Error} When API call fails or returns invalid data
   */
  const fetchModalities = useCallback(async (): Promise<void> => {
    try {
      setModalityList({ loading: true, data: [], error: null });

      const res: APIResponse<Modality[]> = await makeGetCall('/get-modality-list');

      if (res.data?.success !== false && Array.isArray(res.data?.data)) {
        if (isMountedRef.current) {
          setModalityList({
            data: res.data.data,
            loading: false,
            error: null
          });
        }
      } else {
        throw new Error(res.data?.message || 'Invalid response format');
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to load modalities';
      console.error('Error fetching modalities:', error);

      if (isMountedRef.current) {
        setModalityList({
          data: [],
          loading: false,
          error: errorMessage
        });
      }

      message.error(`Error loading modalities: ${errorMessage}`);
    }
  }, []);

  /**
   * Fetches the list of units/locations from the server
   *
   * @async
   * @function fetchUnits
   * @returns {Promise<void>} Promise that resolves when units are loaded
   * @throws {Error} When API call fails or returns invalid data
   */
  const fetchUnits = useCallback(async (): Promise<void> => {
    try {
      setLoadingStates(prev => ({ ...prev, units: true, error: null }));

      const res: APIResponse<Unit[]> = await makePostCall('/units-list');
      const units: Unit[] = res.data?.data || [];

      if (!Array.isArray(units)) {
        throw new Error('Invalid units data format');
      }

      setUnitsList(units);

      // Create mapping with proper type safety
      const mapping: Record<string, string> = units.reduce((acc, unit) => {
        if (unit.unit_code && unit.unit_label) {
          acc[unit.unit_code] = unit.unit_label;
        }
        return acc;
      }, {} as Record<string, string>);

      setUnitsMapping(mapping);
      setLoadingStates(prev => ({ ...prev, units: false }));
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to load units';
      console.error('Error fetching units:', error);

      setLoadingStates(prev => ({
        ...prev,
        units: false,
        error: errorMessage
      }));

      message.error(`Error loading units: ${errorMessage}`);
    }
  }, []);

  useEffect(() => {
    // Cancel previous requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    const loadData = async () => {
      try {
        await Promise.all([fetchModalities(), fetchUnits()]);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error loading initial data:', error);
        }
      }
    };

    loadData();

    // Cleanup function
    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchModalities, fetchUnits]);

  /**
   * Deletes a modality/node from the system
   *
   * @async
   * @function deleteModality
   * @param {Modality} node - The modality object to delete
   * @returns {Promise<void>} Promise that resolves when deletion is complete
   * @throws {Error} When deletion fails or node data is invalid
   */
  const deleteModality = useCallback(async (node: Modality): Promise<void> => {
    if (!node?.en_id) {
      message.error('Invalid modality data');
      return;
    }

    try {
      setDeleteInProgress(true);

      const res: APIResponse<any> = await makePostCall('/delete-modality', {
        en_id: node.en_id
      });

      if (res.data?.success) {
        message.success('Modality deleted successfully');
        await fetchModalities();
      } else {
        throw new Error(res.data?.message || 'Failed to delete modality');
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to delete modality';
      console.error('Error deleting modality:', error);
      message.error(errorMessage);
    } finally {
      setDeleteInProgress(false);
    }
  }, [fetchModalities]);

  const openEditModal = useCallback((record: Modality): void => {
    setSelectedModality(record);
    setEditModalVisible(true);
  }, []);

  /**
   * Handles form submission for editing a modality
   *
   * @async
   * @function handleEditSubmit
   * @param {Partial<Modality & { user_id: string }>} updatedData - Updated modality data
   * @returns {Promise<void>} Promise that resolves when update is complete
   * @throws {Error} When update fails or data validation fails
   */
  const handleEditSubmit = useCallback(async (updatedData: Partial<Modality & { user_id: string }>): Promise<void> => {
    if (!updatedData || typeof updatedData !== 'object') {
      message.error('Invalid form data');
      return;
    }

    try {
      const userDetails: UserDetails = getUserDetails();

      if (!userDetails?.username) {
        throw new Error('User authentication required');
      }

      const res: APIResponse<any> = await makePostCall('/add-modality', {
        ...updatedData,
        user_id: userDetails.username,
      });

      if (res.data?.success) {
        message.success('Modality updated successfully');
        setEditModalVisible(false);
        await fetchModalities();
      } else {
        throw new Error(res.data?.message || 'Failed to update modality');
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to update modality';
      console.error('Error updating modality:', error);
      message.error(errorMessage);
    }
  }, [fetchModalities]);

  /**
   * Memoized filtered modalities based on search and filter criteria
   *
   * @function filteredModalities
   * @returns {Modality[]} Filtered array of modalities
   */
  const filteredModalities = useMemo((): Modality[] => {
    let filtered = modalityList.data;

    // Filter by search text (name, IP, AE title) - optimized with useMemo
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter(modality => {
        const name = (modality.en_node_name || '').toLowerCase();
        const ip = (modality.en_ip_address || '').toLowerCase();
        const aeTitle = (modality.en_ae_title || '').toLowerCase();

        return name.includes(searchLower) ||
               ip.includes(searchLower) ||
               aeTitle.includes(searchLower);
      });
    }

    // Filter by unit
    if (selectedUnit) {
      filtered = filtered.filter(modality => modality.en_location === selectedUnit);
    }

    // Filter by operation
    if (selectedOperation) {
      filtered = filtered.filter(modality => modality.en_operations === selectedOperation);
    }

    return filtered;
  }, [modalityList.data, searchText, selectedUnit, selectedOperation]);

  const handleCloseEditModal = useCallback(() => {
    setEditModalVisible(false);
    setSelectedModality(null);
  }, []);

  // Clear filters function
  const clearFilters = useCallback((): void => {
    setSearchText('');
    setSelectedUnit(null);
    setSelectedOperation(null);
  }, []);

  // Memoized retry handler
  const handleRetry = useCallback(async (): Promise<void> => {
    try {
      await Promise.all([fetchModalities(), fetchUnits()]);
    } catch (error) {
      console.error('Error during retry:', error);
    }
  }, [fetchModalities, fetchUnits]);

  const COLUMNS = useMemo((): TableColumnsType<Modality> => [
    { title: 'Name', dataIndex: 'en_node_name' },
    { title: 'IP', dataIndex: 'en_ip_address' },
    { title: 'Port', dataIndex: 'en_port' },
    { title: 'AE Title', dataIndex: 'en_ae_title' },
    {
      title: 'Location',
      dataIndex: 'en_location',
      render: (val) => unitsMapping[val] || 'NA',
    },
    { title: 'Operations', dataIndex: 'en_operations' },
    { title: 'Comments', dataIndex: 'en_comments' },
    {
      title: 'Actions',
      render: (_, record) => (
        <div className="d-flex gap-2">
          <Button
            icon={<EditOutlined />}
            onClick={() => openEditModal(record)}
            disabled={deleteInProgress}
            title="Edit Node"
          />
          <Popconfirm
            title="Are you sure you want to delete this modality?"
            description="This action cannot be undone."
            onConfirm={() => deleteModality(record)}
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <Button
              icon={<DeleteOutlined />}
              danger
              disabled={deleteInProgress}
              title="Delete Node"
            />
          </Popconfirm>
        </div>
      ),
    },
  ], [unitsMapping, openEditModal, deleteModality, deleteInProgress]);

  const isLoading = modalityList.loading || deleteInProgress || loadingStates.units;
  const loadingMessage = deleteInProgress ? "Processing modality..." : modalityList.loading ? "Loading modalities..." : "Loading data...";

  return (
    <ErrorBoundary>
      <Spin spinning={isLoading} tip={loadingMessage}>
        <div className="nodes-list admin-container">
          {(modalityList.error || loadingStates.error) && (
            <div className="error-container">
              <strong>Error:</strong> {modalityList.error || loadingStates.error}
              <Button
                type="link"
                size="small"
                onClick={handleRetry}
                className="retry-button"
              >
                Retry
              </Button>
            </div>
          )}
          <div>
            <Card
              bordered={false}
              style={{ width: '100%', margin: '1rem auto' }}
              className="admin-card"
            >
              {/* Title and Filter Controls in same row */}
              <div className="nodes-header admin-header">
                <div className="page-title admin-page-title">MODALITIES</div>
                <div className="nodes-filters-inline admin-filters-inline">
                  <Search
                    placeholder="Search by name, IP, or AE title"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    style={{ width: 300, marginRight: 16 }}
                    prefix={<SearchOutlined />}
                    aria-label="Search modalities by name, IP address, or AE title"
                    allowClear
                  />

                  <Select
                    placeholder="Filter by Location"
                    value={selectedUnit}
                    onChange={setSelectedUnit}
                    style={{ width: 150, marginRight: 16 }}
                    allowClear
                    aria-label="Filter modalities by location"
                  >
                    {unitsList.map(unit => (
                      <Option key={unit.unit_code || unit.value} value={unit.unit_code || unit.value}>
                        {unit.unit_label || unit.label}
                      </Option>
                    ))}
                  </Select>

                  <Select
                    placeholder="Filter by Operation"
                    value={selectedOperation}
                    onChange={setSelectedOperation}
                    style={{ width: 150, marginRight: 16 }}
                    allowClear
                    aria-label="Filter modalities by operation type"
                  >
                    <Option value="push">Push</Option>
                  </Select>

                  <Button
                    onClick={clearFilters}
                    aria-label="Clear all active filters"
                  >
                    Clear Filters
                  </Button>
                </div>
              </div>
              <Table
                className="nodes-list-table admin-table"
                columns={COLUMNS}
                dataSource={filteredModalities}
                rowKey="en_id"
                aria-label="Modalities management table"
                pagination={{
                  pageSize: 10,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showTotal: (total, range) =>
                    `${range[0]}-${range[1]} of ${total} modalities`,
                  pageSizeOptions: ['10', '20', '50', '100'],
                  showLessItems: true,
                }}
                locale={{
                  emptyText: searchText || selectedUnit || selectedOperation
                    ? 'No modalities match the current filters'
                    : 'No modalities found'
                }}
              />
            </Card>
          </div>
        </div>

        <EditNode
          visible={editModalVisible}
          nodeData={selectedModality}
          onCancel={handleCloseEditModal}
          onSubmit={handleEditSubmit}
          unitsList={unitsList}
        />
      </Spin>
    </ErrorBoundary>
  );
};

export default ModalityList;
